import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MapPin, Wifi, Coffee, Camera, Lock, ArrowLeft } from "lucide-react";
import { formatINR, formatHour, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { formatApiError } from "@/lib/format";

export default function HubDetail() {
  const { hubId } = useParams();
  const nav = useNavigate();
  const [hub, setHub] = useState(null);
  const [kind, setKind] = useState("seat");
  const [selectedSeatType, setSelectedSeatType] = useState("hot_desk");
  const [selectedDeviceType, setSelectedDeviceType] = useState("mac_mini");
  const [date, setDate] = useState(todayISO());
  const [startHour, setStartHour] = useState(10);
  const [duration, setDuration] = useState(1);
  const [blocked, setBlocked] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/hubs/${hubId}`);
      setHub(data);
    })();
  }, [hubId]);

  useEffect(() => {
    if (!hub) return;
    (async () => {
      const { data } = await api.get(
        `/hubs/${hubId}/availability?date_str=${date}&start_hour=${startHour}&duration_hours=${duration}`
      );
      setBlocked(data.blocked_resource_ids || []);
    })();
    setSelectedResource(null);
  }, [hub, hubId, date, startHour, duration, kind, selectedSeatType, selectedDeviceType]);

  const resources = useMemo(() => {
    if (!hub) return [];
    return kind === "seat"
      ? hub.seats.filter((s) => s.seat_type === selectedSeatType)
      : hub.devices.filter((d) => d.type === selectedDeviceType);
  }, [hub, kind, selectedSeatType, selectedDeviceType]);

  const pricing = hub?.pricing || {};
  const currentType = kind === "seat" ? selectedSeatType : selectedDeviceType;
  const rate = pricing[currentType] || 100;
  const total = rate * duration;

  const book = async () => {
    if (!selectedResource) return;
    setBusy(true);
    try {
      await api.post("/bookings", {
        hub_id: hubId,
        resource_id: selectedResource,
        resource_kind: kind,
        date,
        start_hour: startHour,
        duration_hours: duration,
      });
      toast.success("Booked! Check My Bookings to see the QR.");
      nav("/app/bookings");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!hub)
    return <div className="text-zinc-500">Loading hub…</div>;

  return (
    <div className="space-y-8" data-testid="hub-detail">
      <button
        onClick={() => nav(-1)}
        className="text-zinc-400 hover:text-white inline-flex items-center gap-2 text-sm"
        data-testid="hub-detail-back"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
          {hub.format} Hub
        </div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-2">
          {hub.name}
        </h1>
        <div className="text-zinc-500 flex items-center gap-2">
          <MapPin size={14} /> {hub.address}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {[
          { i: <Wifi size={18} />, t: "Fiber internet" },
          { i: <Coffee size={18} />, t: "Coffee bar" },
          { i: <Camera size={18} />, t: "CCTV monitored" },
          { i: <Lock size={18} />, t: "Lockers" },
        ].map((a) => (
          <div
            key={a.t}
            className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 flex items-center gap-3"
          >
            <span className="text-[#E8D6A3]">{a.i}</span>
            <span className="text-sm">{a.t}</span>
          </div>
        ))}
      </div>

      {/* Booking configurator */}
      <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-8">
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <div>
            <Label>Booking type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="booking-kind-select" className="mt-2 bg-white/5 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="seat">Coworking seat</SelectItem>
                <SelectItem value="device">Mac / PS5</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {kind === "seat" ? (
            <div>
              <Label>Seat type</Label>
              <Select value={selectedSeatType} onValueChange={setSelectedSeatType}>
                <SelectTrigger data-testid="booking-seat-type-select" className="mt-2 bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...new Set(hub.seats.map((s) => s.seat_type))].map((t) => (
                    <SelectItem key={t} value={t}>
                      {t.replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label>Device type</Label>
              <Select value={selectedDeviceType} onValueChange={setSelectedDeviceType}>
                <SelectTrigger data-testid="booking-device-type-select" className="mt-2 bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...new Set(hub.devices.map((d) => d.type))].map((t) => (
                    <SelectItem key={t} value={t}>
                      {t.toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Date</Label>
            <input
              type="date"
              value={date}
              min={todayISO()}
              onChange={(e) => setDate(e.target.value)}
              data-testid="booking-date-input"
              className="mt-2 w-full h-10 px-3 rounded-md bg-white/5 border border-white/10 text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start</Label>
              <Select value={String(startHour)} onValueChange={(v) => setStartHour(+v)}>
                <SelectTrigger data-testid="booking-start-hour-select" className="mt-2 bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 15 }, (_, i) => i + 8).map((h) => (
                    <SelectItem key={h} value={String(h)}>
                      {formatHour(h)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duration</Label>
              <Select value={String(duration)} onValueChange={(v) => setDuration(+v)}>
                <SelectTrigger data-testid="booking-duration-select" className="mt-2 bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 6, 8].map((h) => (
                    <SelectItem key={h} value={String(h)}>
                      {h}h
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div>
          <Label>Pick a {kind === "seat" ? "seat" : "device"}</Label>
          <div className="mt-3 grid grid-cols-4 md:grid-cols-8 gap-2">
            {resources.length === 0 && (
              <div className="col-span-full text-zinc-500 text-sm">None available for this hub.</div>
            )}
            {resources.map((r) => {
              const isBlocked = blocked.includes(r.id);
              const isSelected = selectedResource === r.id;
              const label = kind === "seat" ? r.code : r.asset_id;
              return (
                <button
                  key={r.id}
                  type="button"
                  disabled={isBlocked}
                  data-testid={`resource-tile-${label}`}
                  onClick={() => setSelectedResource(r.id)}
                  className={`aspect-square rounded-xl border text-xs font-bold flex items-center justify-center transition ${
                    isBlocked
                      ? "border-white/5 bg-white/[0.02] text-zinc-700 line-through cursor-not-allowed"
                      : isSelected
                      ? "border-[#D4B878] bg-[#D4B878] text-black"
                      : "border-white/10 bg-white/[0.03] text-zinc-300 hover:border-[#D4B878]/50"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">
              Rate
            </div>
            <div className="text-2xl font-black">
              {formatINR(rate)}
              <span className="text-sm text-zinc-500 font-semibold">/hr</span>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">
              Total ({duration}h)
            </div>
            <div className="text-3xl font-black text-[#E8D6A3]">{formatINR(total)}</div>
          </div>
          <Button
            onClick={book}
            disabled={!selectedResource || busy}
            data-testid="booking-confirm-btn"
            className="bg-[#D4B878] hover:bg-[#E8D6A3] rounded-full px-8 py-6 font-bold"
          >
            {busy ? "Booking…" : "Confirm & pay"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">
      {children}
    </div>
  );
}
